import { deepSetObject } from '@domain-group/javascript-utils'

import {
  Box, Form, SectionDivider, Text,
} from '@domain-group/suit-theme'

import type {
  ModalState, SetModalState,
} from '@modals/AssignCampaignQuoteItemDatesModal/hooks/useCampaignQuoteItemDatesModal'

import type { CampaignQuoteItemModel, CampaignQuoteSectionModel } from '@models/types'

interface UpdateRequiredByDateParams {
  state: ModalState,
  setState: SetModalState,
  updatedItem: { id: number, required_by: string | undefined },
}

const updateRequiredByDate = (params: UpdateRequiredByDateParams) => {
  const { state, setState, updatedItem } = params

  const updatedState = deepSetObject(
    state,
    `campaignQuoteItems.${updatedItem.id}.required_by`,
    updatedItem.required_by,
  ) as ModalState

  setState(() => updatedState)
}

interface QuoteSectionProps {
  campaignQuoteSection: CampaignQuoteSectionModel,
  filteredCampaignQuoteItems: CampaignQuoteItemModel[],
  hasCampaignQuoteItemsWithoutPublications: boolean,
  sortedCampaignQuoteItemIds: number[],
  state: ModalState,
  setState: SetModalState,
}

const QuoteSection = (props: QuoteSectionProps) => {
  const {
    campaignQuoteSection: { title },
    filteredCampaignQuoteItems,
    hasCampaignQuoteItemsWithoutPublications,
    sortedCampaignQuoteItemIds,
    state,
    setState,
  } = props

  const { campaignQuoteItems } = state

  const filteredIdsSet = new Set(filteredCampaignQuoteItems.map(item => item.id))

  const sortedQuoteItemIdsWithoutPublications = sortedCampaignQuoteItemIds.filter(id => filteredIdsSet.has(id))

  return (
    !!sortedQuoteItemIdsWithoutPublications.length && (
      <Box flexDirection="column" marginTop="large">
        <SectionDivider>{title}</SectionDivider>

        <Box flexDirection="column" marginTop="small">
          {hasCampaignQuoteItemsWithoutPublications && (
            sortedQuoteItemIdsWithoutPublications.map((id) => {
              const item = filteredCampaignQuoteItems.find(campaignQuoteItem => campaignQuoteItem.id === id)

              const itemTitle = () => {
                if (item?.publication_id) return item.display_title
                if (item?.quantity && item?.quantity > 1) return `${item?.quantity} x ${item?.title}`

                return item?.title || ''
              }

              const requiredByValue = campaignQuoteItems[id]?.required_by || ''

              return (
                <Box key={`item-${id}`} justifyContent="space-between" alignItems="center" marginY="medium">
                  <Box flexDirection="column" maxWidth="50%">
                    <Text
                      key={`title-${id}`}
                      fontSize="small"
                      marginRight="medium"
                      lineHeight="1.25"
                      variant="ellipsis"
                    >
                      {itemTitle()}
                    </Text>

                    <Text
                      key={`subtitle-${id}`}
                      color="bodyFontLightColor"
                      fontSize="xsmall"
                      marginRight="medium"
                      lineHeight="1.25"
                      variant="ellipsis"
                    >
                      {item?.row_title || item?.options_string || ''}
                    </Text>
                  </Box>

                  <Form.Field boxProps={{ width: '150px' }}>
                    <input
                      type="date"
                      value={requiredByValue}
                      onChange={event => updateRequiredByDate({
                        state,
                        setState,
                        updatedItem: {
                          id,
                          required_by: event.target.value || '',
                        },
                      })}
                    />
                  </Form.Field>
                </Box>
              )
            })
          )}

          {!hasCampaignQuoteItemsWithoutPublications && (
            <Text fontSize="small" color="grey">
              No items in this section.
            </Text>
          )}
        </Box>
      </Box>
    )
  )
}

export default QuoteSection
